import { FunctionComponent, useEffect, useRef, useState } from "react";
import InputMask from "react-input-mask";
import { useNavigate, useParams } from "react-router-dom";
import { useCheckout } from "../../context/CheckoutContext";
import styles from "../../pages/CheckoutPayment/CheckoutPagamentoCartao.module.css";
import ApiService from "../../service/ApiService";
import CartCard from "../cartCard/CartCard";
import { useReCaptchaValidation } from "../../hook/useReCaptchaValidation";
import { ecommerceEvents } from "../../utils/gtm";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface FormPaymentProps {
  onSwitch: (type: string) => void;
  openModal: () => void;
  setDisabled: (value: boolean) => void;
  disabled: boolean;
  googleSiteKey: string;
  cvvTooltip: string;
  expirationTooltip: string;
}

const FormPayment: FunctionComponent<FormPaymentProps> = ({
  onSwitch,
  openModal,
  setDisabled,
  disabled,
  googleSiteKey,
  cvvTooltip,
  expirationTooltip,
}) => {
  const apiService = new ApiService();
  const navigate = useNavigate();
  const params = useParams();
  const { totalPrice, setCard, coupon, address, purchaser, card, cart, planId, planName, clientAndAffiliate } = useCheckout();
  const {
    isValidReCaptcha,
    validateReCaptcha,
  } = useReCaptchaValidation();


  const [splitValue, setSplitValue] = useState(() => {
    if (coupon.found?.discount_value) {
      return coupon.found.discount_type === 0
        ? totalPrice * (1 - parseInt(coupon.found.discount_value) / 100)
        : totalPrice - parseInt(coupon.found.discount_value);
    }
    return totalPrice;
  });
  const [isCartCardVisible, setIsCartCardVisible] = useState(false);
  const cartCardRef = useRef(null);

  const [errors, setErros] = useState({
    cepError: "",
    allFieldsError: "",
    expirationDateError: "",
  });
  const width = window.innerWidth;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (cartCardRef.current && !cartCardRef.current.contains(event.target)) {
        setIsCartCardVisible(false);
      }
    };
    if (isCartCardVisible) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isCartCardVisible]);

  useEffect(() => {
    setSplitValue(() => {
      if (coupon.found?.discount_value) {
        return coupon.found.discount_type === 0
          ? totalPrice * (1 - parseInt(coupon.found.discount_value) / 100)
          : totalPrice - parseInt(coupon.found.discount_value);
      }
      return totalPrice;
    });
  }, [totalPrice, coupon]);

  const handleOnChange = ({ target: { value, name } }) => {
    setCard({ ...card, [name]: value.replace(/_/g, '') });
  };

  useEffect(() => {
    if (isValidReCaptcha) {
      
      ecommerceEvents.addPaymentInfo({
        currency: "BRL",
        value: totalPrice,
        shipping_tier: "free",
        coupon: coupon.name? coupon.name : "",
        email: purchaser.email,
        phone: purchaser.telephone.replace(/[()\-\s]/g, ''),
        first_name: purchaser.name,
        last_name: purchaser.lastname,
        afiliado: params.slug? params.slug : clientAndAffiliate.affiliate_id? clientAndAffiliate.affiliate_id : "",
        payment_type: "Cartão",
        items: cart.map(item => ({
          item_id: item.id,
          item_name: item.name,
          price: item.price,
          quantity: item.quantity,
          promotion_id: planId,
          promotion_name: planName,
        })),
        shipping_address: {
          city: address.cidade,
          street: address.logradouro,
          number: address.numero,
          state: address.estado,
          neighborhood: address.bairro,
          postal_code: address.cep,
          complement: address.complemento,
        }
      });

      params.slug
        ? navigate(`/checkout-pagamento-processando/${params.slug}`)
        : navigate("/checkout-pagamento-processando");
    }
  }, [isValidReCaptcha])

  const handleSubmitForm = async () => {
    await validateReCaptcha();

  };

  const openCartCardModal = async () => {
    await validateReCaptcha();
  };

  useEffect(() => {
    const newErrors = {
      allFieldsError: "",
      expirationDateError: "",
    };
    let shouldBeDisabled = false;

    // 1. Check if all fields are filled (maintaining original logic).
    if (Object.values(card).some((x) => {
      if (x && typeof x === 'string') {
        return x.replace(/[^a-zA-Z0-9\-\_]/g, '') === "";
      } else if (typeof x === 'number' && x !== 0) {
        return false;
      }
      return true;
    })) {
      newErrors.allFieldsError = "Preencha todos os campos";
    }

    // 2. Add the new expiration date validation.
    if (card.expiration_date && card.expiration_date.replace(/\D/g, "").length === 4) {
      const [monthStr, yearStr] = card.expiration_date.split("/");
      const month = parseInt(monthStr, 10);
      const year = parseInt(yearStr, 10);
      const now = new Date();
      const currentMonth = now.getMonth() + 1; // getMonth is 0-11
      const currentYear = now.getFullYear() % 100; // get 2-digit year

      if (year < currentYear || (year === currentYear && month < currentMonth)) {
        newErrors.expirationDateError = "Data de validade inválida.";
      } else if (month < 1 || month > 12) {
        newErrors.expirationDateError = "Mês de validade inválido.";
      }
    }

    // 3. Disable button if any error message is present or if field lengths are incorrect
    if (newErrors.allFieldsError || newErrors.expirationDateError || card.card_number?.replace(/\D/g, "").length < 16 || card.expiration_date?.replace(/\D/g, "").length < 4 || card.security_code?.replace(/\D/g, "").length < 3) {
      setDisabled(true);
    } else {
      setDisabled(false);
    }

    // Set errors state
    setErros(prev => ({ ...prev, ...newErrors }));
  }, [card, setDisabled]);

  return (
    <>
      <div className={styles.dados}>
        <div className={styles.cartaopix}>
          <div
            className={styles.buttonsPagamento}
            onClick={() => onSwitch("Cartão")}
          >
            <img className={styles.icones2} alt="" src="/icones2.svg" />
            {width < 480 ? (
              <div className={styles.div}>Crédito</div>
            ) : (
              <div className={styles.div}>Cartão de crédito</div>
            )}
          </div>
          <div
            className={styles.buttonsPagamento1}
            onClick={() => onSwitch("Boleto")}
          >
            <img className={styles.icones3} alt="" src="/codigo-de-barras.png" />
            <div className={styles.div}>Boleto</div>
          </div>
          <div
            className={styles.buttonsPagamento1}
            onClick={() => onSwitch("Pix")}
          >
            <img className={styles.icones2} alt="" src="/icones12.svg" />
            <div className={styles.div}>PIX</div>
            <img className={styles.pixDiscount} alt="" src="/pix_frame.png" />
          </div>
        </div>
        <div className={styles.numCartaoCredito}>
          <div className={styles.textFields}>
            <div className={styles.div}>
              <span>Número do cartão de crédito</span>
              <span className={styles.span}>*</span>
            </div>
            <InputMask
              mask="9999 9999 9999 9999"
              onChange={handleOnChange}
              name="card_number"
              placeholder="0000 0000 0000 0000"
              className={styles.content}
              value={card.card_number}
            ></InputMask>
          </div>
        </div>
        <div className={styles.tituloCartao}>
          <div className={styles.textFields}>
            <div className={styles.div}>
              <span>Titular do cartão</span>
              <span className={styles.span}>*</span>
            </div>
            <input
              type="text"
              className={styles.content}
              onChange={handleOnChange}
              name="holder_name"
              value={card.holder_name}
              placeholder="Digite o nome escrito no cartão de crédito"
            ></input>
          </div>
        </div>
        <div className={styles.validadecvv}>
          <div className={styles.textFieldsParent}>
            <div className={styles.textFields}>
              <div className={styles.div}>
                <span>Validade</span>
                <span className={styles.span}>*</span>
                <span className={styles.tooltip} title={expirationTooltip}> ? </span>
              </div>
              <InputMask
                mask="99/99"
                onChange={handleOnChange}
                name="expiration_date"
                placeholder="MM/AA"
                value={card.expiration_date}
                className={styles.content}
              ></InputMask>
            </div>
          </div>
          <div className={styles.textFields}>
            <div className={styles.div}>
              <span>CVV</span>
              <span className={styles.span}>*</span>
              <span className={styles.tooltip} title={cvvTooltip}> ? </span>
            </div>
            <InputMask
              mask="9999"
              onChange={handleOnChange}
              name="security_code"
              placeholder="0000"
              className={styles.content}
            />
          </div>
        </div>
        <div className={styles.textFields5}>
          <div className={styles.div}>
            <span>Número de parcelas</span>
            <span className={styles.span}>*</span>
          </div>
          <select
            name="installment"
            className={styles.content5}
            onChange={handleOnChange}
            value={card.installment ? card.installment : 1}
          >
            {" "}
            {Array.from({ length: 12 }, (_, index) => (
              <option key={index + 1} value={index + 1}>
                {`${index + 1} x de R$${(index === 0
                  ? totalPrice
                  : totalPrice / (index + 1)
                ).toFixed(2)}`}
              </option>
            ))}
          </select>
        </div>
        {Object.values(errors).map((x) =>
          x !== "" ? <p className={styles.span} key={x}>{x}*</p> : null
        )}
      </div>
      {width > 800 && (
        <button
          className={!disabled ? styles.cta : styles.disabled}
          disabled={disabled}
          onClick={handleSubmitForm}
        >
        <div className={styles.div}>
          <span className={styles.btnTextoFinalizar}>Finalizar compra</span>
        </div>
        <img className={styles.icones} alt="" src="/icones5.svg" />
      </button>
      )}

      {isCartCardVisible && (
        <div
          className={`${styles.cartCardModal} ${
            isCartCardVisible ? styles.showModal : ""
          }`}
        >
          <div className={styles.cartCardContent} ref={cartCardRef}>
            <CartCard showProducts={true} />
            <div className={styles.btnFinish} onClick={openCartCardModal}>
              <div className={styles.yelly}>Finalizar compra</div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FormPayment;
